import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useApi } from '@/providers/ApiContext';
import { createPlotMarker } from '@/actions/plotMarkers';
import DocumentUploader from '@/components/shared/DocumentUploader';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import type { PlotMarkerCreateRequest, EntityId } from '@/types';

interface PlotMarkerFormProps {
  open: boolean;
  onClose: () => void;
  plotId: string | undefined;
  waypointId: EntityId;
  onMarkerCreated: () => void;
}

function PlotMarkerForm({
  open,
  onClose,
  plotId,
  waypointId,
  onMarkerCreated,
}: PlotMarkerFormProps): React.JSX.Element {
  const api = useApi();
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<boolean>(false);
  const [documents, setDocuments] = useState<
    Array<{
      title: string;
      mime_type: string;
      base64_contents: string;
      type: string;
      pre_processing: string[];
      post_processing: string[];
    }>
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);

  const handleDocumentUpload = useCallback(
    (docs: Array<{
      title: string;
      mime_type: string;
      base64_contents: string;
      type: string;
      pre_processing: string[];
      post_processing: string[];
    }>) => {
      if (docs[0]) {
        setDocuments([docs[0]]);
      }
    },
    [],
  );

  const handleSubmit = async () => {
    if (!name.trim()) {
      setNameError(true);
      return;
    }

    if (documents.length === 0) {
      setApiError('Please upload a document for the marker contents');
      return;
    }

    try {
      setLoading(true);
      setApiError(null);

      const document = documents[0];
      const payload: PlotMarkerCreateRequest = {
        name: name.trim(),
        contents: {
          title: document.title,
          type: document.mime_type,
          base64_contents: document.base64_contents,
        },
      };

      if (!plotId || !api) return;
      await createPlotMarker(api, plotId as EntityId, waypointId, payload);

      // Reset form
      setName('');
      setDocuments([]);
      setNameError(false);

      // Notify parent component
      onMarkerCreated();

      // Close dialog
      onClose();
    } catch (error) {
      console.error('Error creating plot marker:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to create the plot marker. This could be due to network issues, server problems, or invalid data.';
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName('');
      setDocuments([]);
      setNameError(false);
      setApiError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Add Plot Marker</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label='Marker Name'
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(false);
            }}
            error={nameError}
            helperText={nameError ? 'Marker name is required' : ''}
            margin='normal'
            disabled={loading}
          />

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant='subtitle2' gutterBottom>
              Marker Contents
            </Typography>
            <DocumentUploader
              setDocuments={handleDocumentUpload}
              disabled={loading}
              label='Upload Document'
              tooltip='Upload a document to serve as the marker contents'
            />
            {documents.length > 0 && (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                Uploaded: {documents[0].title}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant='contained'
          disabled={!name.trim() || documents.length === 0}
        >
          Create Marker
        </LoadingButton>
      </DialogActions>
      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Dialog>
  );
}

export default PlotMarkerForm;

